<template>
	<div>
		<!-- Clyde CTA will be appended to this div -->
		<div id="clydeCTA"></div>
	</div>
</template>

<script>
export default {
	name: 'ClydeCTA',
	inject: ['store'],
	props: {
		product: {
			type: Object,
			required: true,
		},
		variant: {
			type: Object,
			required: true,
		},
		active: {
			type: Boolean,
			default: undefined,
		},
	},
	methods: {
		fetchClydeScript() {
			if (document.querySelector('script[src="https://js.joinclyde.com/widget.js"]')) {
				console.log('Clyde script already loaded.')
				this.waitForClyde()
				return
			}
			console.log('Setting Clyde script...')
			const script = document.createElement('script')
			script.src = 'https://js.joinclyde.com/widget.js'
			script.async = true
			script.onload = () => this.waitForClyde() // Ensure script is ready before waiting
			document.head.appendChild(script)
		},

		waitForClyde(retries = 15) {
			if (this.clydeScriptLoaded) {
				this.initializeClyde({ sku: this.variant?.bcId, price: this.variant?.calculatedPrice })
				return
			}

			if (retries > 0) {
				setTimeout(() => {
					console.log('Waiting for Clyde Script to Load...', retries, 'tries left')
					this.waitForClyde(retries - 1)
				}, 200)
			} else {
				console.error('Clyde script did not load in time.')
			}
		},

		initializeClyde(productData) {
			console.log('1. Starting Clyde initialization checks...')

			try {
				console.log('2. Initializing Clyde with product data...')
				window.Clyde.init(
					{
						key: 'ck_live_tNcSz8wYwm12YSqr',
						onSelectedContract: (contract) => {
							console.log('Selected contract: ', contract)
							this.$emit('contractSelected', contract)
						},
						onDeselectedContract: () => {
							console.log('Contract removed')
							this.$emit('contractRemoved')
						},
					},
					function () {
						if (window.Clyde.getSettings().productPage) {
							console.log('3. Clyde initialized. Setting active product...')
							window.Clyde.setActiveProduct(productData, function () {
								if (window.Clyde.getActiveProduct().contracts.length === 0) {
									console.log('No contracts available for this product.')
									return
								}
								console.log('4. Active product set. Appending Clyde CTA to the DOM...')
								window.Clyde.appendToSelector('#clydeCTA')
							})
						}
					},
				)
			} catch (error) {
				console.error('Error initializing Clyde: ', error?.trace)
			}
		},

		updateClyde(productData) {
			console.log('Updating Clyde with new product data...')
			try {
				if (!window.Clyde) {
					console.warn('Clyde not loaded, retrying...')
					this.handleClyde()
					return
				}
				window.Clyde.setActiveProduct(productData, function () {
					console.log('Active product updated')
				})
			} catch (error) {
				console.log('Error updating Clyde: ', error?.trace)
			}
		},

		cleanUpClyde() {
			const clydeContainer = document.getElementById('clydeCTA')
			if (clydeContainer) {
				console.log('1.b Removing Clyde container child elements...')
				while (clydeContainer.firstChild) {
					console.log('Removing child: ', clydeContainer.firstChild)
					clydeContainer.removeChild(clydeContainer.firstChild)
				}
				console.log('1.c Clyde container cleaned.')
			} else {
				console.warn('1.b Clyde container (#clydeCTA) not found.')
			}
		},

		resetClyde() {
			console.log('Cleaning up Clyde...')

			// Remove Clyde global variables
			delete window.Clyde
			delete window.__CLYDE_ENV__
			delete window.ClydeDebugger
			Object.keys(window).forEach((key) => {
				if (key.startsWith('Clyde') || key.includes('__CLYDE')) {
					console.log('Deleting global Clyde variable:', key)
					delete window[key]
				}
			})

			// Remove Clyde script
			const clydeScript = document.querySelector('script[src="https://js.joinclyde.com/widget.js"]')
			if (clydeScript) {
				console.log('Removing Clyde script...')
				clydeScript.remove()
			}

			// Remove Clyde-generated iframes
			const clydeIframes = document.querySelectorAll('iframe[name^="clyde-widget"]')
			clydeIframes.forEach((iframe) => {
				console.log('Removing Clyde iframe:', iframe)
				iframe.remove()
			})

			// Remove Clyde modals or containers
			const clydeContainers = document.querySelectorAll('[class*="clyde-"]')
			clydeContainers.forEach((container) => {
				console.log('Removing Clyde container:', container)
				container.remove()
			})

			// Remove Clyde event listeners
			window.removeEventListener('message', () => {})
			window.removeEventListener('scroll', () => {})

			console.log('Clyde cleanup completed.')
		},

		handleClyde() {
			this.resetClyde()
			setTimeout(() => {
				//this.cleanUpClyde()
				this.fetchClydeScript()
			}, 300) // Allow time for cleanup before reloading
		},
	},

	computed: {
		clydeScriptLoaded() {
			return !!window.Clyde
		},
	},

	beforeUnmount() {
		console.log('Removing Clyde from the DOM...')
		this.resetClyde()
	},

	watch: {
		'variant.bcId': {
			handler(newVal, oldVal) {
				console.log(`variant.bcId changed from ${oldVal} to ${newVal}`)
				this.updateClyde({ sku: this.variant.bcId, price: this.variant.calculatedPrice })
			},
			immediate: false,
		},
		active: {
			handler(newVal, oldVal) {
				console.log(`Clyde CTA Component Instance: active changed from ${oldVal} to ${newVal}`)
				if (newVal) {
					console.log('Clyde CTA Component Instance Active, setting up...')
					this.handleClyde()
				} else {
					console.log('Clyde CTA Component Instance Inactive, skipping setup...')
				}
			},
			immediate: false,
		},
	},
}
</script>

<style>
/* Add any Clyde-related styling here */
</style>
